// 購物車「取貨場次」的行為（Horizon 主題版）。
// 做成 custom element，而且要能承受 Horizon 的「原地更新」：
// Horizon 重新渲染購物車時，不一定會換掉整個 <session-picker>，可能只替換裡面的
// <select> 或 JSON，所以這裡不快取任何子元素，事件全部掛在 <session-picker> 本身，
// 並用 MutationObserver 在內容被換掉之後重新同步狀態。
// 這個檔案要在 layout/theme.liquid 載入一次；drawer 重新渲染時插入的 <script> 不會執行。

const MESSAGES = {
  same: '這已經是目前選的場次。',
  full: '這個場次剛剛額滿了，請選別的時段。',
  network: '連線失敗，請再試一次。',
  failed: '更新失敗，請再試一次。',
};

class SessionPicker extends HTMLElement {
  #observer;
  #dataCache = { text: null, value: [] };

  connectedCallback() {
    // 事件掛在自己身上，子元素被換掉也不會失效
    this.addEventListener('change', this);
    this.addEventListener('click', this);

    this.#observer = new MutationObserver(() => this.sync());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-current', 'data-cleanup', 'data-blocked'],
    });

    this.sync();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('change', this);
    this.removeEventListener('click', this);
  }

  // 統一的事件入口（addEventListener 傳物件時會呼叫 handleEvent）
  handleEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (event.type === 'change') {
      if (target.matches('[data-field="location"]')) this.showDates();
      else if (target.matches('[data-field="date"]')) this.showTimes();
      else if (target.matches('[data-field="time"]')) this.confirmButton.disabled = !target.value;
      return;
    }

    if (event.type === 'click') {
      if (target.closest('[data-action="confirm"]')) this.confirm();
      else if (target.closest('[data-action="change"]')) this.showForm();
    }
  }

  // 每次都重新查，不快取：Horizon 可能只換掉子元素
  get locationSelect() { return this.querySelector('[data-field="location"]'); }
  get dateSelect() { return this.querySelector('[data-field="date"]'); }
  get timeSelect() { return this.querySelector('[data-field="time"]'); }
  get confirmButton() { return this.querySelector('[data-action="confirm"]'); }
  get message() { return this.querySelector('[data-message]'); }
  get form() { return this.querySelector('[data-form]'); }
  get selectedBox() { return this.querySelector('[data-selected]'); }
  get current() { return Number(this.dataset.current) || 0; }
  get sectionId() { return this.dataset.sectionId || ''; }

  get data() {
    const text = this.querySelector('[data-session-data]')?.textContent ?? '[]';
    if (text !== this.#dataCache.text) {
      try {
        this.#dataCache = { text, value: JSON.parse(text) };
      } catch (error) {
        console.error('[session-picker] 場次資料解析失敗', error);
        this.#dataCache = { text, value: [] };
      }
    }
    return this.#dataCache.value;
  }

  // 把畫面帶到正確狀態。可以重複呼叫，已經處理過的狀態不會再動。
  sync() {
    try {
      if (this.dataset.cleanup) {
        this.cleanup(Number(this.dataset.cleanup));
        return;
      }
      if (this.hasAttribute('data-blocked')) return;

      const locationSelect = this.locationSelect;
      const dateSelect = this.dateSelect;
      if (!locationSelect || !dateSelect) return;

      // 日期欄還是初始狀態（停用、只有提示文字）才需要初始化
      const untouched = dateSelect.disabled && dateSelect.options.length <= 1;
      if (!untouched) return;

      if (this.current) {
        if (this.form && !this.form.hidden) this.preselect();
        return;
      }

      // 只有一個可選地點時直接選好，讓客人從日期開始選
      const locations = [...locationSelect.options].filter((option) => option.value);
      if (locations.length === 1) {
        locationSelect.value = locations[0].value;
        this.showDates();
      }
    } catch (error) {
      console.error('[session-picker] 同步狀態失敗', error);
    }
  }

  showForm() {
    this.selectedBox?.setAttribute('hidden', '');
    this.form?.removeAttribute('hidden');
    this.preselect();
    if (this.confirmButton) this.confirmButton.disabled = true;
  }

  fill(select, options, placeholder) {
    select.replaceChildren();
    const first = document.createElement('option');
    first.value = '';
    first.textContent = placeholder;
    select.append(first);
    for (const option of options) {
      const el = document.createElement('option');
      el.value = option.value;
      el.textContent = option.label;
      el.disabled = Boolean(option.disabled);
      select.append(el);
    }
    select.disabled = options.length === 0;
  }

  locationOf(handle) {
    return this.data.find((location) => location.handle === handle);
  }

  showDates() {
    const location = this.locationOf(this.locationSelect.value);
    const dates = [...new Set(location ? location.variants.map((v) => v.date) : [])];
    this.fill(this.dateSelect, dates.map((date) => ({ value: date, label: date })), '請選擇日期');
    this.fill(this.timeSelect, [], '請先選日期');
    this.confirmButton.disabled = true;
  }

  showTimes() {
    const location = this.locationOf(this.locationSelect.value);
    const slots = location ? location.variants.filter((v) => v.date === this.dateSelect.value) : [];
    this.fill(
      this.timeSelect,
      slots.map((v) => ({
        value: String(v.id),
        label: v.available ? v.time : `${v.time}（已額滿）`,
        disabled: !v.available,
      })),
      '請選擇時段',
    );
    this.confirmButton.disabled = true;
  }

  preselect() {
    const current = this.current;
    if (!current) return;
    const location = this.data.find((l) => l.variants.some((v) => v.id === current));
    if (!location) return;
    this.locationSelect.value = location.handle;
    this.showDates();
    const variant = location.variants.find((v) => v.id === current);
    this.dateSelect.value = variant.date;
    this.showTimes();
    this.timeSelect.value = String(variant.id);
  }

  setBusy(busy) {
    const button = this.confirmButton;
    if (!button) return;
    button.disabled = busy;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  async confirm() {
    const next = Number(this.timeSelect?.value);
    if (!next) return;
    const current = this.current;
    if (next === current) {
      this.message.textContent = MESSAGES.same;
      return;
    }
    this.message.textContent = '';
    this.setBusy(true);

    // 舊場次設為 0、新場次設為 1，一個請求完成；順便要回這個 section 的新 HTML
    const updates = { [next]: 1 };
    if (current) updates[current] = 0;
    const body = { updates };
    if (this.sectionId) body.sections = this.sectionId;

    let response;
    try {
      response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (_) {
      this.message.textContent = MESSAGES.network;
      this.setBusy(false);
      return;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      this.message.textContent = response.status === 422 ? MESSAGES.full : error.description || MESSAGES.failed;
      this.setBusy(false);
      return;
    }

    const cart = await response.json();
    await this.refresh(cart, next);
  }

  async cleanup(variantId) {
    if (!variantId || SessionPicker.cleaningUp) return;
    SessionPicker.cleaningUp = true;
    try {
      const body = { updates: { [variantId]: 0 } };
      if (this.sectionId) body.sections = this.sectionId;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) await this.refresh(await response.json(), 0);
    } catch (error) {
      console.error('[session-picker] 移除多餘場次失敗', error);
    } finally {
      SessionPicker.cleaningUp = false;
    }
  }

  // 讓購物車畫面反映新的場次。
  // 1. 先走 Horizon 的 cart:update 事件，讓主題自己重新渲染 section（購物車圖示的數量也會跟著更新）。
  // 2. 事件沒有帶來重新渲染時，用回傳的 HTML 換掉內容：drawer 只換 .cart-drawer__inner，
  //    不能換掉外層 <dialog>，否則 drawer 會關起來；購物車頁整個 section 換掉。
  // 3. 兩者都不行才重新載入頁面。
  async refresh(cart, next) {
    try {
      const { CartUpdateEvent } = await import('@theme/events');
      document.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId || 'session-picker', {
          source: 'session-picker',
          itemCount: cart.item_count,
          sections: cart.sections,
        }),
      );
      await new Promise((resolve) => setTimeout(resolve, 600));
      const settled = next
        ? document.querySelector(`session-picker[data-current="${next}"]`)
        : !document.querySelector('session-picker[data-cleanup]');
      if (settled) return;
    } catch (_) {
      // 沒有 @theme/events（不是 Horizon 或版本不同），走下面的手動替換
    }

    const html = cart.sections?.[this.sectionId];
    const target = this.sectionId && document.getElementById(`shopify-section-${this.sectionId}`);
    if (html && target) {
      const freshDoc = new DOMParser().parseFromString(html, 'text/html');

      const inner = target.querySelector('.cart-drawer__inner');
      const freshInner = freshDoc.querySelector('.cart-drawer__inner');
      if (inner && freshInner) {
        inner.replaceWith(freshInner);
        return;
      }

      const freshSection = freshDoc.getElementById(`shopify-section-${this.sectionId}`);
      if (freshSection) {
        target.replaceWith(freshSection);
      } else {
        target.innerHTML = html;
      }
      return;
    }

    window.location.reload();
  }
}

if (!customElements.get('session-picker')) {
  customElements.define('session-picker', SessionPicker);
}